import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { MaskedLine } from "./Reveal";

const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 55, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 55, damping: 16 });

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const stripY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const onMouseMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden grid-lines pt-24 pb-16"
    >
      <div className="absolute inset-0 stage-light pointer-events-none" />

      <div className="relative z-10 px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center w-full max-w-[1600px] mx-auto">
        <motion.div style={{ y: textY }} className="lg:col-span-8">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="font-plex text-xs md:text-sm uppercase tracking-[0.3em] text-volt mb-6 md:mb-8"
            data-testid="hero-overline"
          >
            Performance Nasal Strips — N°001
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-anton uppercase leading-[0.85] tracking-tight text-[clamp(4rem,13.5vw,13rem)]"
          >
            <MaskedLine delay={0.15}>Air in.</MaskedLine>
            <MaskedLine delay={0.3}>
              <span className="text-outline">Doubt</span> out.
            </MaskedLine>
            <MaskedLine delay={0.45}>
              Breethe<span className="text-volt">.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-md text-base md:text-lg font-light text-white/60 leading-relaxed"
            data-testid="hero-subline"
          >
            A matte-black athletic strip that rips your airway open. More oxygen
            on the run, in the gym, and in bed. Make snoring a thing of the past.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.45, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="/buy"
              data-testid="hero-cta-primary"
              className="bg-volt text-black font-anton uppercase text-base md:text-lg px-8 py-4 tracking-wider transition-[transform,background-color] duration-200 hover:bg-volt-dark active:scale-95"
            >
              Buy now
            </a>
            <a
              href="#product"
              data-testid="hero-cta-secondary"
              className="group border border-white/25 text-white font-anton uppercase text-base md:text-lg px-8 py-4 tracking-wider transition-[background-color,color,transform] duration-300 hover:bg-white hover:text-black active:scale-95"
            >
              See N°001
            </a>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-4 flex justify-center lg:justify-end" style={{ perspective: 900 }}>
          <motion.div style={{ y: stripY, rotateX, rotateY, transformStyle: "preserve-3d" }}>
            <motion.div
              initial={{ opacity: 0, y: 80, rotate: -18 }}
              animate={{ opacity: 1, y: 0, rotate: -12 }}
              transition={{ delay: 0.8, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            >
              <motion.div
                animate={{ y: [0, -14, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="relative"
              >
                <div className="absolute inset-0 scale-110 bg-[radial-gradient(ellipse_at_center,_rgba(255,49,49,0.18)_0%,_transparent_65%)] blur-2xl" />
                <img
                  src="/pack-front.png"
                  alt="Breethe N°001 nose strips pack — 30 strips"
                  data-testid="nasal-strip-visual"
                  className="relative w-64 md:w-80 lg:w-96 drop-shadow-[0_40px_50px_rgba(0,0,0,0.8)]"
                />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-6 md:left-10 flex items-center gap-3 font-plex text-[10px] uppercase tracking-[0.3em] text-white/40"
        data-testid="hero-scroll-hint"
      >
        <span className="block w-10 h-px bg-white/30" />
        Scroll — 001
      </motion.div>
    </section>
  );
};

export default Hero;
